// src/components/auth/AuthAction.js
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import { FiCheckCircle, FiXCircle, FiLoader, FiLock, FiEye, FiEyeOff, FiArrowLeft } from 'react-icons/fi';
import { SUPPORT_EMAIL } from '../shared/constants/contactInfo';

const Logo = () => (
  <div className="text-center mb-10 relative z-10 animate-fade-in-down">
    <div className="bg-gradient-to-br from-slate-blue to-warm-dark p-0.5 rounded-3xl inline-block shadow-glow">
      <div className="bg-warm-dark rounded-[22px] p-4">
        <h1 className="font-sans font-black text-4xl uppercase tracking-tighter text-white">
          Next<span className="text-slate-blue">AInsight</span>
        </h1>
      </div>
    </div>
  </div>
);

function AuthAction() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading | form | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    if (mode !== 'resetPassword') {
      setErrorMessage('This action is not supported. Please use the app directly.');
      setStatus('error');
      return;
    }

    if (!oobCode) {
      setErrorMessage('Invalid link. The reset code is missing.');
      setStatus('error');
      return;
    }

    let cancelled = false;

    verifyPasswordResetCode(auth, oobCode)
      .then(() => {
        if (!cancelled) setStatus('form');
      })
      .catch(() => {
        if (!cancelled) {
          setErrorMessage('This password reset link is invalid or has expired.');
          setStatus('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [mode, oobCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (password.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPw) {
      setFormError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('success');
    } catch {
      setFormError('Failed to reset password. The link may have expired. Please request a new one.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-warm-dark text-cream font-sans selection:bg-slate-blue/30 overflow-hidden relative">
      {/* Background Ambience */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-slate-blue/20 rounded-full blur-[120px] opacity-40 mix-blend-screen animate-pulse-slow"></div>
        <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-rosy-taupe/10 rounded-full blur-[100px] opacity-30 mix-blend-screen animate-blob"></div>
      </div>

      <div className="flex-grow flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 relative z-10">
        <Logo />

        {/* Loading State */}
        {status === 'loading' && (
          <div className="max-w-md w-full bg-warm-gray/10 backdrop-blur-xl rounded-[2.5rem] p-10 border border-white/5 shadow-2xl text-center hover:border-white/10 transition-all duration-500">
            <div className="py-10 animate-fade-in">
              <div className="w-24 h-24 bg-slate-blue/10 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-blue border border-slate-blue/30">
                <FiLoader size={48} className="animate-spin" />
              </div>
              <h2 className="text-2xl font-black uppercase tracking-tight text-white mb-3">
                Verifying Link...
              </h2>
              <p className="text-sm text-cream/60">
                Please wait while we verify your reset link.
              </p>
            </div>
          </div>
        )}

        {/* Form State */}
        {status === 'form' && (
          <div className="max-w-md w-full space-y-8 bg-warm-gray/10 backdrop-blur-xl rounded-[2.5rem] p-10 border border-white/5 shadow-2xl relative group hover:border-white/10 transition-all duration-500">
            <div className="text-center">
              <h2 className="text-2xl font-black text-white tracking-tight mb-2">
                Set New Password
              </h2>
              <p className="mt-2 text-sm text-cream/60">
                Enter your new password below.
              </p>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* New Password */}
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <FiLock className="h-5 w-5 text-slate-blue group-focus-within:text-white transition-colors" />
                  </div>
                  <label htmlFor="new-password" className="sr-only">New Password</label>
                  <input
                    id="new-password" name="password" type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password" required
                    className="block w-full pl-12 pr-12 py-4 bg-black/20 border border-white/5 rounded-2xl text-cream placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-slate-blue/50 focus:border-transparent transition-all"
                    placeholder="New Password" value={password}
                    onChange={(e) => setPassword(e.target.value)} disabled={submitting}
                  />
                  <button
                    type="button" tabIndex={-1}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-cream/40 hover:text-cream/70 transition-colors"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                  </button>
                </div>

                {/* Confirm Password */}
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <FiLock className="h-5 w-5 text-slate-blue group-focus-within:text-white transition-colors" />
                  </div>
                  <label htmlFor="confirm-password" className="sr-only">Confirm Password</label>
                  <input
                    id="confirm-password" name="confirmPassword" type={showConfirmPw ? 'text' : 'password'}
                    autoComplete="new-password" required
                    className="block w-full pl-12 pr-12 py-4 bg-black/20 border border-white/5 rounded-2xl text-cream placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-slate-blue/50 focus:border-transparent transition-all"
                    placeholder="Confirm Password" value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)} disabled={submitting}
                  />
                  <button
                    type="button" tabIndex={-1}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-cream/40 hover:text-cream/70 transition-colors"
                    onClick={() => setShowConfirmPw((v) => !v)}
                  >
                    {showConfirmPw ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Form Error */}
              {formError && (
                <div className="p-4 rounded-xl text-sm text-center font-medium border animate-fade-in bg-red-500/10 border-red-500/20 text-red-200">
                  {formError}
                </div>
              )}

              <button
                type="submit" disabled={submitting}
                className="group relative w-full flex justify-center py-4 px-4 border border-transparent text-sm font-bold uppercase tracking-wider rounded-2xl text-warm-dark bg-slate-blue hover:bg-slate-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-blue shadow-glow transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin -ml-1 h-5 w-5 text-warm-dark" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Resetting...
                  </span>
                ) : 'Reset Password'}
              </button>
            </form>

            {/* Back to Login Link */}
            <div className="text-center pt-6 border-t border-white/5 mt-6">
              <Link to="/login" className="inline-flex items-center gap-2 font-bold text-slate-blue hover:text-white transition-colors uppercase tracking-wide text-xs group">
                <FiArrowLeft className="group-hover:-translate-x-1 transition-transform" />
                Back to Login
              </Link>
            </div>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="max-w-md w-full bg-warm-gray/10 backdrop-blur-xl rounded-[2.5rem] p-10 border border-white/5 shadow-2xl text-center hover:border-white/10 transition-all duration-500">
            <div className="py-10 animate-fade-in">
              <div className="w-24 h-24 bg-pastel-green-bg/20 rounded-full flex items-center justify-center mx-auto mb-6 text-pastel-green border border-pastel-green/30 shadow-glow">
                <FiCheckCircle size={48} />
              </div>
              <h2 className="text-2xl font-black uppercase tracking-tight text-white mb-3">
                Password Reset
              </h2>
              <p className="text-sm text-cream/60 leading-relaxed mb-8">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-8 py-3 bg-slate-blue text-warm-dark rounded-xl text-sm font-bold uppercase tracking-wide hover:bg-slate-blue/90 transition-all shadow-glow"
              >
                Back to Login
              </Link>
            </div>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="max-w-md w-full bg-warm-gray/10 backdrop-blur-xl rounded-[2.5rem] p-10 border border-white/5 shadow-2xl text-center hover:border-white/10 transition-all duration-500">
            <div className="py-10 animate-fade-in">
              <div className="w-24 h-24 bg-soft-coral/10 rounded-full flex items-center justify-center mx-auto mb-6 text-soft-coral border border-soft-coral/30">
                <FiXCircle size={48} />
              </div>
              <h2 className="text-2xl font-black uppercase tracking-tight text-white mb-3">
                Link Invalid or Expired
              </h2>
              <p className="text-sm text-cream/60 leading-relaxed mb-8">
                {errorMessage}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/reset-password"
                  className="inline-flex items-center justify-center px-8 py-3 bg-slate-blue text-warm-dark rounded-xl text-sm font-bold uppercase tracking-wide hover:bg-slate-blue/90 transition-all"
                >
                  Request New Link
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-8 py-3 border border-white/10 rounded-xl text-sm font-bold text-white hover:bg-white/10 transition-all uppercase tracking-wide"
                >
                  Go to Login
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Minimal Footer */}
      <div className="absolute bottom-6 w-full text-center z-10">
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-xs text-cream/20 hover:text-slate-blue transition-colors">Need help? {SUPPORT_EMAIL}</a>
      </div>
    </div>
  );
}

export default AuthAction;
